import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, formatDateTR } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { RotateCw, ArrowLeft, Truck, Search, Package, LayoutGrid, Printer, ScanLine, Camera, Download, CheckCircle2 } from "lucide-react";
import BarcodeCameraScanner from "../components/BarcodeCameraScanner";

const CATS = ["Baza", "Başlık", "Yatak", "Komodin", "Bench"];

const CELL_CLASS = {
  WAITING:       "bg-slate-100 text-slate-500 border-slate-300",
  IN_SHIPMENT:   "bg-amber-100 text-amber-900 border-amber-500",
  PARTIAL:       "bg-blue-50 text-blue-800 border-blue-400",
  SHIPPED:       "bg-emerald-500 text-white border-emerald-700",
  EMPTY:         "bg-slate-50 text-slate-300 border-slate-200",
};
const LABELS = {
  WAITING: "Bekliyor",
  IN_SHIPMENT: "Sevk Bekliyor",
  PARTIAL: "Kısmen",
  SHIPPED: "Sevk Edildi",
};

export default function ShipmentPanelPage() {
  const { id } = useParams();
  return id ? <ShipmentDetailPanel id={id} /> : <ShipmentListPanel />;
}

function ShipmentListPanel() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shipments");
      setShipments(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    if (!s) return shipments;
    return shipments.filter(sh =>
      sh.shipment_number.toLowerCase().includes(s) ||
      (sh.customer_name || "").toLowerCase().includes(s) ||
      (sh.destination || "").toLowerCase().includes(s)
    );
  }, [shipments, q]);

  return (
    <div>
      <PageHeader
        title="Sevkiyat Paneli"
        subtitle="Bir sevkiyat seçin — barkod okumaları anlık yansır."
        actions={
          <button onClick={load} data-testid="ship-panel-refresh" className="inline-flex items-center gap-2 px-4 py-2 border-2 border-slate-800 hover:bg-slate-900 hover:text-white font-bold text-sm uppercase">
            <RotateCw size={16}/> Yenile
          </button>
        }
      />

      <div className="mb-4 relative max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ship-panel-search"
          placeholder="Sevkiyat / Müşteri / Varış ara..."
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 focus:border-blue-700 outline-none"/>
      </div>

      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100 border-b-2 border-slate-300 text-xs uppercase tracking-widest text-slate-600">
              <th className="text-left px-3 py-3 font-bold">Sevkiyat No</th>
              <th className="text-left px-3 py-3 font-bold">Müşteri</th>
              <th className="text-left px-3 py-3 font-bold">Varış</th>
              <th className="text-left px-3 py-3 font-bold">Tarih</th>
              <th className="text-right px-3 py-3 font-bold">İlerleme</th>
              <th className="text-left px-3 py-3 font-bold">Durum</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Yükleniyor...</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-slate-500">
                <Truck size={40} className="mx-auto mb-2 opacity-40" />
                Sevkiyat yok.
              </td></tr>
            )}
            {filtered.map((s, i) => {
              const progress = s.expected > 0 ? Math.round((s.scanned / s.expected) * 100) : 0;
              return (
                <tr key={s.id} onClick={() => navigate(`/sevkiyat-paneli/${s.id}`)}
                  data-testid={`ship-panel-row-${s.shipment_number}`}
                  className={`border-b border-slate-200 cursor-pointer ${i % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-blue-50`}>
                  <td className="px-3 py-3 font-mono font-bold text-blue-700">{s.shipment_number}</td>
                  <td className="px-3 py-3 font-semibold">{s.customer_name}</td>
                  <td className="px-3 py-3">{s.destination}</td>
                  <td className="px-3 py-3 font-mono text-xs">{formatDateTR(s.shipment_date)}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-24 bg-slate-200 h-2">
                        <div className={`h-2 ${progress === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${progress}%` }}/>
                      </div>
                      <span className="font-mono text-xs tabular-nums">{s.scanned}/{s.expected}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-block px-2 py-1 text-[10px] font-bold uppercase border-2 ${s.status === "SEVK_EDILDI" ? "bg-emerald-100 text-emerald-800 border-emerald-500" : "bg-amber-100 text-amber-800 border-amber-500"}`}>
                      {s.status === "SEVK_EDILDI" ? "Sevk Edildi" : "Hazırlanıyor"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ShipmentDetailPanel({ id }) {
  const [rows, setRows] = useState([]);
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [barcode, setBarcode] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: matrix }, { data: sh }] = await Promise.all([
        api.get(`/shipments/${id}/status-matrix`),
        api.get(`/shipments/${id}`),
      ]);
      setRows(matrix);
      setShipment(sh);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [id]);

  const doScan = async (bc) => {
    const code = (bc ?? barcode).trim();
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/shipments/${id}/scan`, { barcode: code });
      toast.success(data.message || "Sevk edildi");
      setLastScan({ ok: true, ...data });
      setBarcode("");
      load();
    } catch (e) {
      const msg = formatApiError(e);
      toast.error(msg);
      setLastScan({ ok: false, message: msg });
    }
    setBusy(false);
  };

  const doExcel = async () => {
    try {
      const res = await api.get(`/shipments/${id}/excel`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `sevkiyat_${shipment?.shipment_number || id}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const shippedTotal = rows.reduce((s, r) => s + Object.values(r.categories || {}).reduce((a, c) => a + (c?.shipped_count || 0), 0), 0);
  const totalPieces = rows.reduce((s, r) => s + Object.values(r.categories || {}).reduce((a, c) => a + (c?.total || 0), 0), 0);

  return (
    <div>
      <Link to="/sevkiyat-paneli" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-blue-700 mb-3">
        <ArrowLeft size={16}/> Sevkiyat Listesine Dön
      </Link>
      <PageHeader
        title={shipment ? `Sevkiyat ${shipment.shipment_number}` : "Sevkiyat Paneli"}
        subtitle={shipment ? `${shipment.customer_name} · ${shipment.destination} · ${formatDateTR(shipment.shipment_date)}` : ""}
        actions={
          <div className="flex gap-2 flex-wrap">
            <Link to={`/sevkiyat-listesi/yazdir?shipmentId=${id}`} data-testid="ship-panel-print"
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-black text-white font-bold text-sm uppercase">
              <Printer size={16}/> Sevkiyat Listesi PDF
            </Link>
            <button onClick={doExcel} data-testid="ship-panel-excel"
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm uppercase">
              <Download size={16}/> Excel İndir
            </button>
            <button onClick={load} data-testid="ship-panel-detail-refresh" className="inline-flex items-center gap-2 px-4 py-2 border-2 border-slate-800 hover:bg-slate-900 hover:text-white font-bold text-sm uppercase">
              <RotateCw size={16}/> Yenile
            </button>
          </div>
        }
      />

      {/* Barcode scan bar */}
      <div className="card-industrial p-4 mb-4 bg-slate-900 text-white">
        <div className="flex flex-wrap items-center gap-2">
          <ScanLine size={20}/>
          <div className="text-xs uppercase font-bold tracking-widest opacity-80 mr-2">Barkod Okut</div>
          <input
            autoFocus
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doScan()}
            placeholder="Barkodu okutun veya yazın..."
            data-testid="ship-panel-scan-input"
            className="flex-1 min-w-[200px] px-3 py-2.5 bg-white text-slate-900 font-mono border-2 border-slate-700 focus:border-emerald-500 outline-none"
          />
          <button onClick={() => doScan()} disabled={busy} data-testid="ship-panel-scan-btn"
            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold text-sm uppercase inline-flex items-center gap-1">
            <CheckCircle2 size={16}/> Sevk Et
          </button>
          <button onClick={() => setScannerOpen(true)} data-testid="ship-panel-camera-btn"
            className="px-4 py-2.5 border-2 border-slate-700 hover:border-white text-white font-bold text-sm uppercase inline-flex items-center gap-1">
            <Camera size={16}/>
          </button>
        </div>
        {lastScan && (
          <div className={`mt-2 text-sm ${lastScan.ok ? "text-emerald-300" : "text-red-300"}`} data-testid="ship-panel-last-scan">
            {lastScan.ok
              ? <>✓ Sevk edildi: <b className="font-mono">{lastScan.piece?.barcode}</b> · {lastScan.piece?.model} · {lastScan.piece?.size}</>
              : <>✗ {lastScan.message}</>}
          </div>
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="card-industrial px-4 py-2">
          <div className="text-[10px] uppercase font-bold text-slate-500">Toplam</div>
          <div className="font-display font-black text-2xl tabular-nums" data-testid="ship-panel-total">{totalPieces}</div>
        </div>
        <div className="card-industrial px-4 py-2 border-emerald-500">
          <div className="text-[10px] uppercase font-bold text-emerald-700">Sevk Edilen</div>
          <div className="font-display font-black text-2xl tabular-nums text-emerald-700" data-testid="ship-panel-shipped">{shippedTotal}</div>
        </div>
        <div className="card-industrial px-4 py-2">
          <div className="text-[10px] uppercase font-bold text-slate-500">Kalan</div>
          <div className="font-display font-black text-2xl tabular-nums text-amber-700" data-testid="ship-panel-remaining">{totalPieces - shippedTotal}</div>
        </div>
      </div>

      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm" data-testid="ship-panel-table">
          <thead>
            <tr className="bg-slate-100 border-b-2 border-slate-300 text-xs uppercase tracking-widest text-slate-600">
              <th className="text-left px-3 py-3 font-bold">Sipariş</th>
              <th className="text-left px-3 py-3 font-bold">Müşteri</th>
              <th className="text-left px-3 py-3 font-bold">Referans</th>
              <th className="text-left px-3 py-3 font-bold">Ölçü</th>
              <th className="text-left px-3 py-3 font-bold">Kumaş</th>
              {CATS.map(c => <th key={c} className="text-left px-2 py-3 font-bold">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5 + CATS.length} className="p-8 text-center text-slate-500">Yükleniyor...</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5 + CATS.length} className="p-10 text-center text-slate-500">
                <LayoutGrid size={40} className="mx-auto mb-2 opacity-40"/>
                Bu sevkiyata dahil sipariş yok.
              </td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={r.id} className={`border-b border-slate-200 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-blue-50`}
                data-testid={`ship-panel-row-${r.order_number}`}>
                <td className="px-3 py-2 font-mono font-bold text-blue-700">{r.order_number}</td>
                <td className="px-3 py-2 font-semibold">{r.customer_name}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.reference || "—"}</td>
                <td className="px-3 py-2 font-mono">{r.size || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.fabric || "—"}</td>
                {CATS.map(c => {
                  const cat = r.categories?.[c];
                  return <CategoryCell key={c} cat={cat} order={r.order_number} category={c} />;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5" data-testid="ship-panel-legend">
        {Object.entries(LABELS).map(([k, l]) => (
          <span key={k} className={`px-2 py-1 border-2 text-[10px] font-bold uppercase ${CELL_CLASS[k]}`}>{l}</span>
        ))}
      </div>

      {scannerOpen && (
        <BarcodeCameraScanner autoStart fullScreen
          onDetected={(code) => { setScannerOpen(false); doScan(code); }}
          onClose={() => setScannerOpen(false)} />
      )}
    </div>
  );
}

function CategoryCell({ cat, order, category }) {
  if (!cat || !cat.total) {
    return <td className={`px-2 py-2 border-l border-slate-100 text-center text-xs ${CELL_CLASS.EMPTY}`}>—</td>;
  }
  const total = cat.total;
  const shipped = cat.shipped_count || 0;
  const inShip = cat.in_shipment_count || 0;

  let key = "WAITING";
  if (shipped === total) key = "SHIPPED";
  else if (shipped > 0) key = "PARTIAL";
  else if (inShip > 0) key = "IN_SHIPMENT";

  return (
    <td className="px-1 py-2 border-l border-slate-100">
      <div
        data-testid={`ship-panel-cell-${order}-${category}`}
        className={`px-2 py-1.5 border-2 ${CELL_CLASS[key]} text-xs font-bold leading-tight`}
        title={`${cat.model} · ${shipped}/${total} sevk edildi`}
      >
        <div className="font-display font-black text-[13px] truncate">{cat.model || "•"}</div>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wide opacity-90 mt-0.5">
          <span>{LABELS[key]}</span>
          <span className="font-mono tabular-nums">{shipped}/{total}</span>
        </div>
      </div>
    </td>
  );
}
